/**
 * 📋 AffiliateLeadsPage - Lead-Verwaltung
 * 
 * Features:
 * - Filterable/Sortable Table
 * - Status Badges (Neu, In Bearbeitung, Kontaktiert, Konvertiert, Abgelehnt)
 * - Bulk Actions
 * - Lead Detail Modal (placeholder)
 */

import React from 'react';
import { AffiliateLayout } from '@/components/affiliate/layout/AffiliateLayout';
import { StatusBadge } from '@/components/affiliate/badges/StatusBadge';
import { 
  SkeletonStatCard,
  SkeletonTable 
} from '@/components/affiliate/skeletons';
import { 
  EmptyStateSearchNoResults, 
  EmptyStateNoLeads 
} from '@/components/affiliate/empty/EmptyState';
import { useAffiliateLeads } from '@/features/affiliate/hooks';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, 
  MoreHorizontal, 
  Plus, 
  Download,
  Eye,
  Edit,
  Trash2,
  Mail,
  Phone,
  Users,
  Target,
  CheckCircle2,
  TrendingUp
} from 'lucide-react';
import type { LeadStatus, LeadSource } from '@/lib/affiliate-mock-data';

// ============================================
// STATS BAR
// ============================================

interface LeadStatsBarProps {
  stats: {
    total: number;
    openLeads: number;
    wonThisMonth: number;
    conversionRate: number;
  };
}

const LeadStatsBar: React.FC<LeadStatsBarProps> = ({ stats }) => {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
      <Card className="p-4 border-0 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <Users className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Gesamt Leads</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
        </div>
      </Card>
      <Card className="p-4 border-0 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Target className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Offene Leads</p>
            <p className="text-2xl font-bold text-blue-600">{stats.openLeads}</p>
          </div>
        </div>
      </Card>
      <Card className="p-4 border-0 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Gewonnen (Monat)</p>
            <p className="text-2xl font-bold text-green-600">{stats.wonThisMonth}</p>
          </div>
        </div>
      </Card>
      <Card className="p-4 border-0 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <TrendingUp className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Conversion Rate</p>
            <p className="text-2xl font-bold text-primary">{stats.conversionRate}%</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ============================================
// MAIN PAGE
// ============================================

export const AffiliateLeadsPage: React.FC = () => {
  const { toast } = useToast();
  const { 
    leads, 
    stats, 
    isLoading, 
    filters, 
    pagination,
    setSearch, 
    setStatusFilter, 
    setSourceFilter,
    setPage,
    getStatusBadge,
    getSourceLabel,
  } = useAffiliateLeads();

  // Loading State
  if (isLoading) {
    return (
      <AffiliateLayout
        pageTitle="Leads"
        pageSubtitle="Verwalte deine geworbenen Restaurant-Leads"
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[1, 2, 3, 4].map(i => <SkeletonStatCard key={i} />)}
        </div>
        <SkeletonTable rows={6} columns={7} />
      </AffiliateLayout>
    );
  }

  const hasNoLeads = leads.length === 0 && !filters.search && filters.status === 'all' && filters.source === 'all';
  const hasNoResults = leads.length === 0 && (filters.search || filters.status !== 'all' || filters.source !== 'all');

  return (
    <AffiliateLayout
      pageTitle="Leads"
      pageSubtitle="Verwalte deine geworbenen Restaurant-Leads"
      headerActions={
        <Button className="bg-primary hover:bg-primary/90">
          <Plus className="w-4 h-4 mr-2" />
          Lead hinzufügen
        </Button>
      }
    >
      {/* Stats Bar */}
      <LeadStatsBar stats={stats} />

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Suche nach Restaurant, Kontakt..."
                value={filters.search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            
            {/* Filter Dropdowns */}
            <div className="flex gap-2">
              <Select value={filters.status} onValueChange={(v) => setStatusFilter(v as LeadStatus | 'all')}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  <SelectItem value="neu">Neu</SelectItem>
                  <SelectItem value="kontaktiert">Kontaktiert</SelectItem>
                  <SelectItem value="interessiert">Interessiert</SelectItem>
                  <SelectItem value="verhandlung">Verhandlung</SelectItem>
                  <SelectItem value="gewonnen">Gewonnen</SelectItem>
                  <SelectItem value="verloren">Verloren</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filters.source} onValueChange={(v) => setSourceFilter(v as LeadSource | 'all')}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Quelle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Quellen</SelectItem>
                  <SelectItem value="website">Website</SelectItem>
                  <SelectItem value="referral">Empfehlung</SelectItem>
                  <SelectItem value="cold-call">Kaltakquise</SelectItem>
                  <SelectItem value="event">Event</SelectItem>
                  <SelectItem value="social">Social Media</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="outline" size="sm" className="h-10 w-10 p-0">
                <Download className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="px-0">
          {/* Empty States */}
          {hasNoLeads && (
            <div className="py-8">
              <EmptyStateNoLeads onAddLead={() => toast({ title: '📥 Lead hinzufügen', description: 'Lead-Formular wird bald verfügbar sein.' })} />
            </div>
          )}
          
          {hasNoResults && (
            <div className="py-8">
              <EmptyStateSearchNoResults 
                query={filters.search || undefined}
                onClear={() => {
                  setSearch('');
                  setStatusFilter('all');
                  setSourceFilter('all');
                }}
              />
            </div>
          )}

          {/* Table */}
          {leads.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50/50">
                      <TableHead className="w-[180px] font-semibold">Restaurant</TableHead>
                      <TableHead className="font-semibold">Kontakt</TableHead>
                      <TableHead className="font-semibold">Quelle</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="text-right font-semibold">Erstellt</TableHead>
                      <TableHead className="w-[50px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((lead) => {
                      const statusConfig = getStatusBadge(lead.status);
                      return (
                        <TableRow key={lead.id} className="cursor-pointer hover:bg-gray-50/50">
                          <TableCell>
                            <p className="font-medium text-gray-900">{lead.restaurant}</p>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="text-sm font-medium">{lead.contact}</p>
                              <p className="text-xs text-gray-500">{lead.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-600">{getSourceLabel(lead.source)}</span>
                          </TableCell>
                          <TableCell>
                            <StatusBadge variant={statusConfig.variant} label={statusConfig.label} size="sm" />
                          </TableCell>
                          <TableCell className="text-right text-sm text-gray-500">
                            {new Date(lead.createdAt).toLocaleDateString('de-DE')}
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Details
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Bearbeiten
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                  <Mail className="w-4 h-4 mr-2" />
                                  E-Mail senden
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                  <Phone className="w-4 h-4 mr-2" />
                                  Anrufen
                                </DropdownMenuItem>
                                <DropdownMenuItem className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Löschen
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  {pagination.totalItems} Lead{pagination.totalItems !== 1 ? 's' : ''} insgesamt
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={pagination.page <= 1}
                    onClick={() => setPage(pagination.page - 1)}
                  >
                    Zurück
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => setPage(pagination.page + 1)}
                  >
                    Weiter
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </AffiliateLayout>
  );
};

export default AffiliateLeadsPage;
